import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { AdminShell } from '../../components/AdminShell';
import { loadAllShops, mrrFor } from '../../lib/adminQueries';

const fmtMoney = (n) => {
  const v = Number(n || 0);
  if (v >= 1000) return `£${(v / 1000).toFixed(1)}k`;
  return `£${v.toFixed(0)}`;
};

const Sparkline = ({ values, max, color = 'var(--acc)' }) => {
  if (!values?.length) return null;
  const w = 100, h = 40;
  const step = w / Math.max(1, values.length - 1);
  const pts = values.map((v, i) => `${i * step},${h - (v / max) * h}`).join(' ');
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ width: '100%', height: '100%' }}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="2" />
    </svg>
  );
};

const RevenueInner = () => {
  const [loading, setLoading] = useState(true);
  const [shops, setShops] = useState([]);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    (async () => {
      const all = await loadAllShops();
      const { data: s } = await supabase.from('platform_settings').select('*').maybeSingle();
      setSettings(s);
      setShops(all);
      setLoading(false);
    })();
  }, []);

  // Active subscriptions
  const active = useMemo(
    () => shops.filter(s => ['active', 'trialing'].includes(s.subscription_status)),
    [shops]
  );
  const totalMrr = useMemo(
    () => active.reduce((s, sh) => s + mrrFor(sh, settings), 0),
    [active, settings]
  );
  const arpu = active.length ? totalMrr / active.length : 0;

  const standardCount = active.filter(s => s.plan === 'standard').length;
  const premiumCount  = active.filter(s => s.plan === 'premium').length;
  const standardMrr   = active.filter(s => s.plan === 'standard').reduce((s, sh) => s + mrrFor(sh, settings), 0);
  const premiumMrr    = active.filter(s => s.plan === 'premium').reduce((s, sh) => s + mrrFor(sh, settings), 0);
  const standardPct   = active.length ? (standardCount / active.length) * 100 : 0;
  const premiumPct    = active.length ? (premiumCount  / active.length) * 100 : 0;

  // MRR-growth chart — derive an estimate from `current_period_end` cohorts.
  // We synthesise the last 6 month-end points by counting how many of today's
  // active subs would have been active at each historical month.
  const months = useMemo(() => {
    const arr = [];
    const today = new Date();
    for (let i = 5; i >= 0; i--) {
      const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
      arr.push(d);
    }
    return arr;
  }, []);
  const growth = useMemo(() => months.map(m => {
    // Count subs whose stripe_subscription start (proxied by created_at) was before m's end
    const cutoff = new Date(m.getFullYear(), m.getMonth() + 1, 0).getTime();
    const subsAtM = active.filter(sh => new Date(sh.created_at).getTime() <= cutoff);
    return subsAtM.reduce((s, sh) => s + mrrFor(sh, settings), 0);
  }), [months, active, settings]);
  const maxGrowth = Math.max(1, ...growth);

  return (
    <main className="main admin-page" data-testid="admin-revenue-page">
      <div className="topbar">
        <div>
          <h2>Revenue</h2>
          <div className="sub">MRR · ARPU · plan mix</div>
        </div>
      </div>

      <div className="body">
        <div className="a-kpis" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 13, marginBottom: 14 }}>
          <div className="kpi" data-testid="rev-kpi-mrr">
            <div className="t">Monthly recurring revenue</div>
            <div className="v">{fmtMoney(totalMrr)}</div>
          </div>
          <div className="kpi" data-testid="rev-kpi-arpu">
            <div className="t">ARPU</div>
            <div className="v">{fmtMoney(arpu)}<small style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-mute-2)' }}>/shop</small></div>
          </div>
          <div className="kpi" data-testid="rev-kpi-subs">
            <div className="t">Paying shops</div>
            <div className="v">{active.length}</div>
          </div>
        </div>

        <div className="panel" data-testid="rev-chart">
          <h3 className="admin-info-header">MRR growth · last 6 months</h3>
          <div className="rchart">
            <div className="area">
              {loading ? null : <Sparkline values={growth} max={maxGrowth} />}
            </div>
            <div className="legend" style={{ paddingTop: 6 }}>
              {months.map((d, i) => (
                <span key={i}>
                  <span className="sw" style={{ background: 'var(--acc)' }} />
                  {d.toLocaleString('en-GB', { month: 'short' })} · {fmtMoney(growth[i] || 0)}
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="panel" style={{ marginTop: 14 }} data-testid="rev-tier-mix">
          <h3 className="admin-info-header">Plan mix</h3>
          <div className="mixrow">
            <span className="nm"><span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--mute)' }} /> Standard</span>
            <div className="track"><div className="fl" style={{ width: `${standardPct}%`, background: 'var(--mute)' }} /></div>
            <span className="vv">{standardCount} · {fmtMoney(standardMrr)}</span>
          </div>
          <div className="mixrow">
            <span className="nm"><span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--acc)' }} /> Premium</span>
            <div className="track"><div className="fl" style={{ width: `${premiumPct}%`, background: 'var(--acc)' }} /></div>
            <span className="vv">{premiumCount} · {fmtMoney(premiumMrr)}</span>
          </div>
        </div>
      </div>
    </main>
  );
};

const Revenue = () => <AdminShell><RevenueInner /></AdminShell>;
export default Revenue;
